import React from 'react';
import { Box, Button, FormControl, IconButton, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { CalcMethod, METHOD_LABEL, Milestone, RuleParams, Tier, money } from './bonusTypes';

/** The rule being typed: the method and the numbers it needs. Shared by "New program" and "Change rule". */
export interface RuleDraft {
  method: CalcMethod;
  value: string;
  tiers: Array<{ from: string; amount: string }>;
  metricLabel: string;
  milestones: Array<{ label: string; days: string; amount: string }>;
}

export const emptyRule = (): RuleDraft => ({ method: 'FIXED', value: '', tiers: [{ from: '', amount: '' }], metricLabel: '', milestones: [{ label: '', days: '', amount: '' }] });

export const ruleParams = (r: RuleDraft): RuleParams => {
  if (r.method === 'FIXED') return { amount: Number(r.value) };
  if (r.method === 'QUANTITY_RATE') return { rate: Number(r.value) };
  if (r.method === 'PERCENTAGE') return { percent: Number(r.value) };
  if (r.method === 'TIERED') return { tiers: r.tiers.map((t) => ({ from: Number(t.from), amount: Number(t.amount) })), ...(r.metricLabel.trim() ? { metricLabel: r.metricLabel.trim() } : {}) };
  if (r.method === 'MILESTONE') return { milestones: r.milestones.map((m) => ({ label: m.label.trim(), days: Number(m.days), amount: Number(m.amount) })) };
  return {};
};

/** Enough typed to send? The server checks everything again. */
export const ruleReady = (r: RuleDraft): boolean => {
  if (r.method === 'MANUAL') return true;
  if (r.method === 'MILESTONE') return r.milestones.length > 0 && r.milestones.every((m) => Number.isInteger(Number(m.days)) && Number(m.days) > 0 && Number(m.amount) > 0);
  if (r.method === 'TIERED') return r.tiers.length > 0 && r.tiers.every((t) => t.from !== '' && Number(t.from) >= 0 && Number(t.amount) > 0);
  return Number(r.value) > 0;
};

export const ruleText = (method: CalcMethod, params: RuleParams, currency: string): string => {
  if (method === 'FIXED') return `${money(params.amount || 0, currency)} per case`;
  if (method === 'QUANTITY_RATE') return `${money(params.rate || 0, currency)} each`;
  if (method === 'PERCENTAGE') return `${params.percent ?? 0}% of a base amount`;
  if (method === 'TIERED') return (params.tiers || []).map((t: Tier) => `${t.from}+ pays ${money(t.amount, currency)}`).join(' · ') || 'Levels by result';
  if (method === 'MILESTONE') return (params.milestones || []).map((m: Milestone) => `${m.label}: ${money(m.amount, currency)}`).join(' · ') || 'Milestones after the hire';
  return 'Typed by the supervisor';
};

/** The wording for the single number typed per earning. */
export const inputLabel = (method: CalcMethod | null, params: RuleParams): string =>
  method === 'PERCENTAGE' ? 'Base amount' : method === 'TIERED' ? (params.metricLabel || 'Result') : 'Qty';

/** What a sample would pay. Example only: it uses the numbers typed in the form, not an approved rate. */
export function sampleResult(r: RuleDraft, quantity: string, min: string, max: string): string {
  const v = Number(r.value);
  let amount: number | null = null;
  let how = '';
  if (r.method === 'FIXED' && v > 0) { amount = v; how = `Each case pays ${v.toFixed(2)}`; }
  if (r.method === 'QUANTITY_RATE' && v > 0 && Number(quantity) > 0) { amount = Math.round(Number(quantity) * v * 100) / 100; how = `${quantity} x ${v.toFixed(2)} = ${amount.toFixed(2)}`; }
  if (r.method === 'PERCENTAGE' && v > 0 && Number(quantity) > 0) { amount = Math.round(Number(quantity) * v) / 100; how = `${v}% of ${quantity} = ${amount.toFixed(2)}`; }
  if (r.method === 'TIERED' && ruleReady(r) && quantity !== '') {
    const tiers = r.tiers.map((t) => ({ from: Number(t.from), amount: Number(t.amount) })).sort((a, b) => a.from - b.from);
    const reached = tiers.filter((t) => t.from <= Number(quantity)).pop();
    if (!reached) return `${quantity} does not reach the first level (${tiers[0].from}), so nothing is earned`;
    amount = reached.amount;
    how = `${quantity} reaches the level from ${reached.from}: ${amount.toFixed(2)}`;
  }
  if (r.method === 'MILESTONE') return ruleReady(r) ? `The referral earns ${r.milestones.map((m) => `${m.label.trim() ? `${m.label.trim()} (day ${m.days})` : `day ${m.days}`}: ${Number(m.amount).toFixed(2)}`).join(', ')}` : 'Enter the days and amounts above.';
  if (amount === null) return r.method === 'MANUAL' ? 'The supervisor types each amount.' : 'Enter the numbers above.';
  if (min && amount < Number(min)) return `${amount.toFixed(2)} is below the minimum, so this would be refused`;
  if (max && amount > Number(max)) return `${amount.toFixed(2)} is above the maximum, so this would be refused`;
  return how;
}

interface Props {
  value: RuleDraft;
  onChange: (patch: Partial<RuleDraft>) => void;
  /** Wording: "New amount per case" in the change dialog. */
  fresh?: boolean;
  sampleQty: string;
  onSampleQty: (v: string) => void;
}

const RuleFields: React.FC<Props> = ({ value, onChange, fresh, sampleQty, onSampleQty }) => {
  const prefix = fresh ? 'New ' : '';
  const setMilestone = (i: number, patch: Partial<{ label: string; days: string; amount: string }>) => onChange({ milestones: value.milestones.map((m, j) => (j === i ? { ...m, ...patch } : m)) });
  const setTier = (i: number, patch: Partial<{ from: string; amount: string }>) => onChange({ tiers: value.tiers.map((t, j) => (j === i ? { ...t, ...patch } : t)) });
  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <FormControl fullWidth size="small">
          <InputLabel>Method</InputLabel>
          <Select label="Method" value={value.method} onChange={(e) => onChange({ method: e.target.value as CalcMethod })}>
            {(Object.keys(METHOD_LABEL) as CalcMethod[]).map((m) => <MenuItem key={m} value={m}>{METHOD_LABEL[m]}</MenuItem>)}
          </Select>
        </FormControl>
        {(value.method === 'FIXED' || value.method === 'QUANTITY_RATE' || value.method === 'PERCENTAGE') && (
          <TextField size="small" type="number" required fullWidth value={value.value} onChange={(e) => onChange({ value: e.target.value })}
            label={value.method === 'FIXED' ? `${prefix}${fresh ? 'amount' : 'Amount'} per case` : value.method === 'PERCENTAGE' ? `${prefix}${fresh ? 'percent' : 'Percent'} (1-100)` : `${prefix}${fresh ? 'rate' : 'Rate'} for each one`} />
        )}
        {(value.method === 'QUANTITY_RATE' || value.method === 'PERCENTAGE' || value.method === 'TIERED') && (
          <TextField size="small" type="number" label={value.method === 'PERCENTAGE' ? 'Sample base amount' : value.method === 'TIERED' ? 'Sample result' : 'Sample quantity'}
            value={sampleQty} onChange={(e) => onSampleQty(e.target.value)} sx={{ minWidth: 170 }} />
        )}
      </Stack>
      {value.method === 'MILESTONE' && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Each milestone is paid some days after the referred person is hired, if they are still employed then. The days must rise from one milestone to the next.
          </Typography>
          {value.milestones.map((m, i) => (
            <Stack key={i} direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center">
              <TextField size="small" label="Name (optional)" value={m.label} onChange={(e) => setMilestone(i, { label: e.target.value })} sx={{ width: 190 }} />
              <TextField size="small" type="number" label="Days after the hire" value={m.days} onChange={(e) => setMilestone(i, { days: e.target.value })} sx={{ width: 170 }} inputProps={{ min: 1, step: 1 }} />
              <TextField size="small" type="number" label="Pays" value={m.amount} onChange={(e) => setMilestone(i, { amount: e.target.value })} sx={{ width: 130 }} />
              <IconButton size="small" aria-label="Remove milestone" disabled={value.milestones.length === 1} onClick={() => onChange({ milestones: value.milestones.filter((_, j) => j !== i) })}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Button size="small" onClick={() => onChange({ milestones: [...value.milestones, { label: '', days: '', amount: '' }] })}>Add a milestone</Button>
        </Box>
      )}
      {value.method === 'TIERED' && (
        <Box>
          <TextField size="small" label="What is measured (optional, for example PTP kept %)" value={value.metricLabel} onChange={(e) => onChange({ metricLabel: e.target.value })} fullWidth sx={{ mb: 1.5 }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Each level starts at a result and pays an amount. The highest level the typed result reaches pays. A result below the first level pays nothing.
          </Typography>
          {value.tiers.map((t, i) => (
            <Stack key={i} direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center">
              <TextField size="small" type="number" label="Result from" value={t.from} onChange={(e) => setTier(i, { from: e.target.value })} sx={{ width: 150 }} />
              <TextField size="small" type="number" label="Pays" value={t.amount} onChange={(e) => setTier(i, { amount: e.target.value })} sx={{ width: 150 }} />
              <IconButton size="small" aria-label="Remove level" disabled={value.tiers.length === 1} onClick={() => onChange({ tiers: value.tiers.filter((_, j) => j !== i) })}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Button size="small" onClick={() => onChange({ tiers: [...value.tiers, { from: '', amount: '' }] })}>Add a level</Button>
        </Box>
      )}
    </Stack>
  );
};

export default RuleFields;
